/**
 * Synced from convex/lib/canonicalRefs.ts (WP-345: canonical refs, NG4: MCP import boundary).
 * GENERATED by scripts/generate-canonical-refs-copies.mjs — DO NOT EDIT BY HAND.
 * Run `npm run refs:sync` to regenerate after editing the source.
 */
/**
 * Canonical Refs — pure parser for semantic ref syntax.
 *
 * WP-640 S2 (TASK-63): THIS FILE IS THE SINGLE SOURCE. `packages/cli/src/lib/canonicalRefs.ts`
 * and `packages/mcp-server/src/lib/canonicalRefs.ts` are GENERATED mirrors (scripts/generate-
 * canonical-refs-copies.mjs) — never hand-edit them. Edit here, then `npm run refs:sync` (writes
 * both copies) and `npm run refs:sync:check` to verify (no drift). Folding all three into a real
 * shared package (e.g. `kernel`, which already declares "ZERO Convex imports" and is already a
 * `cli` devDependency) was considered and DECLINED: `scripts/check-mcp-boundaries.mjs` (BR-25,
 * TEN-174) unconditionally fails any `packages/mcp-server/src` file importing from outside the
 * package — a live `kernel` import there is structurally blocked, not just risky — and a new
 * kernel/ folder also needs a WP-537 kind-manifest row. This generate+check pair reuses the SAME
 * "copy-free at the source-editing level, physically-copied at the build level" pattern
 * `scripts/generate-mcp-embedded-copies.mjs` already established for flags_core.ts/logo-markup.ts
 * for exactly this MCP-boundary reason — not a new mechanism, the established one. It collapses
 * the "3 independently hand-copy-pasted files" problem the doneWhen names (a human previously had
 * to remember to edit three files identically) down to ONE edited source with caught drift. NG4
 * (WP-345) is UNCHANGED: CLI/MCP still must not import the resolver (convex/canonicalRefs.ts, no
 * `lib/`) — only this pure parser.
 *
 * Zero Convex imports. This file is drift-synced to:
 *   - packages/cli/src/lib/canonicalRefs.ts (CLI copy — identical logic)
 *   - packages/mcp-server/src/lib/canonicalRefs.ts (MCP copy — identical logic)
 *
 * NG4 (WP-345): CLI/MCP MUST NOT import the resolver (convex/canonicalRefs.ts).
 * They import only this pure parser module.
 *
 * DEC-A/WP-354: semantic refs syntax is {{ref:canonicalKey}},
 * {{ref:canonicalKey:display}}, {{ref:domain:data/analytics}}, or
 * {{ref:entry:DEC-123}}.
 * Chain: WP-345 E4, DEC-K (projection hash trailer)
 *
 * WP-436 S1 — Axis 2 vocabulary resolver (DEC-1021, STD-253):
 * {{vocab:<canonicalKey>.<form>}} tokens + getCollectionLabel resolver.
 * PAT-230: resolver is pure — no Convex import, workspace context passed as arg.
 */

/**
 * WP-354 Build-Order #6: Setup-ref resolver return shape.
 *
 * All consumers (CLI, MCP, handshake, editor health) must read
 * the discriminated kind+status fields instead of an optional resolved object.
 *
 * Spec: docs/system/design/authority-domains-design-spec.md §632
 * Chain: WP-354 Slice H, STD-101 (single source of truth)
 *
 * drift-synced to:
 *   - packages/cli/src/lib/canonicalRefs.ts
 *   - packages/mcp-server/src/lib/canonicalRefs.ts
 */
export type SetupRefResolution = {
	ref: string;
	kind: 'entry' | 'domain' | 'seed' | 'unknown';
	status:
		| 'resolved'            // entry/domain found
		| 'unresolved'          // valid syntax but no match
		| 'unsupported-future'  // seed:* — future scope
		| 'invalid'             // ref couldn't be parsed
		;
	/** Human-readable entryId (e.g. 'BR-5'). Only when status='resolved' for kind='entry' or 'domain'. */
	localEntryId?: string;
	/** Domain slug (e.g. 'engineering/backend'). Only when kind='domain' and status='resolved'. */
	canonicalSlug?: string;
	/** true when kind is 'entry' or 'domain'; false for seed/unknown */
	required: boolean;
	message?: string;
	action?: string;
};

/**
 * Regex for matching semantic refs. Captures: [1] ref key/path, [2] optional display label.
 */
const SEMANTIC_REF_REGEX = /\{\{ref:([a-zA-Z0-9_./-]+(?::[a-zA-Z0-9_./-]+)?)(?::([^}]+))?\}\}/g;

/**
 * Extracts canonical keys from a body string.
 * Handles {{ref:key}} and {{ref:key:display}}.
 *
 * Returns deduplicated array of canonical keys in order of first appearance.
 */
export function parseSemanticRefs(body: string): string[] {
	if (!body || body.length === 0) return [];

	const keys: string[] = [];
	const seen = new Set<string>();

	// Reset lastIndex for global regex before each use
	const regex = new RegExp(SEMANTIC_REF_REGEX.source, 'g');
	let match: RegExpExecArray | null;

	while ((match = regex.exec(body)) !== null) {
		const key = match[1];
		if (key && !seen.has(key)) {
			seen.add(key);
			keys.push(key);
		}
	}

	return keys;
}

/**
 * Renders a resolved ref for display in projected output.
 * Falls back to the raw key wrapped in brackets if no resolved name is available.
 *
 * Examples:
 *   renderSemanticRef(bet, 'Active Bets')  → 'Active Bets'
 *   renderSemanticRef(bet)                 → '[bet]'
 *   renderSemanticRef(bet, undefined)      → '[bet]'
 */
export function renderSemanticRef(key: string, resolvedName?: string): string {
	if (resolvedName && resolvedName.trim().length > 0) {
		return resolvedName.trim();
	}
	return `[${key}]`;
}

/**
 * Replaces all semantic refs in a body string with their resolved display names.
 *
 * resolvedMap: Map<canonicalKey, resolvedName> — keys not in the map are rendered
 * as [unresolved: key] to make missing refs visible.
 */
export function replaceSemanticRefs(body: string, resolvedMap: Map<string, string>): string {
	if (!body || body.length === 0) return body;

	const regex = new RegExp(SEMANTIC_REF_REGEX.source, 'g');

	return body.replace(regex, (_match, key: string, displayLabel: string | undefined) => {
		// If an inline display label is provided, it takes precedence over the resolved name
		if (displayLabel && displayLabel.trim().length > 0) {
			return displayLabel.trim();
		}
		const resolved = resolvedMap.get(key);
		if (resolved) {
			return resolved;
		}
		return `[unresolved: ${key}]`;
	});
}

// ─── Axis 2 Vocabulary Resolver (WP-436 S1) ─────────────────────────────────
// STD-253: {{vocab:<canonicalKey>.<form>}} token grammar.
// PAT-230: pure resolver — no Convex import. WorkspaceCtx is passed as arg.
// DEC-1021: Axis 2 is INDEPENDENT of Axis 1 (displayNames). Never merge.

/**
 * Label set for a single canonical type (Axis 2, DEC-1021).
 * All fields optional — undefined means "derive from next layer".
 */
export type LabelSet = {
	singular?: string;
	plural?: string;
	verb_start?: string;
	verb_commit?: string;
	verb_review?: string;
	verb_complete?: string;
	verb_archive?: string;
};

/** Valid form names for a LabelSet. */
export type LabelForm = keyof LabelSet;

/**
 * Minimal workspace context for Axis 2 resolution.
 * Passed by the caller — keeps the resolver pure (PAT-230).
 */
export type WorkspaceVocabCtx = {
	/** Per-workspace overrides: canonicalKey → LabelSet (partial). */
	collectionLabels?: Record<string, LabelSet | undefined>;
	/**
	 * Layer 2 defaults from system_collection_definitions.labels, keyed by canonicalKey.
	 * Fetched via chain.getVocabulary collectionDefaults and threaded through by the caller.
	 * Used as fallback when no workspace override exists and collectionCtx is not passed.
	 * WP-436 completion — wires dead Layer 2 to consumers.
	 */
	collectionDefaults?: Record<string, LabelSet | undefined>;
};

/**
 * Minimal collection context for Axis 2 resolution.
 * Passed by the caller — keeps the resolver pure (PAT-230).
 */
export type CollectionVocabCtx = {
	/** Collection-level default labels (from system_collection_definitions.labels). */
	labels?: LabelSet;
};

/**
 * Regex for matching vocab tokens: {{vocab:<canonicalKey>.<form>}}
 * STD-253: canonicalKey + form are both lowercase snake_case.
 * Captures: [1] canonicalKey, [2] form.
 */
export const VOCAB_TOKEN_REGEX = /\{\{vocab:([a-z_]+)\.([a-z_]+)\}\}/g;

/**
 * Resolve the display label for a canonical type + form.
 *
 * Resolution chain (DEC-1021):
 *   1. workspaceCtx.collectionLabels[canonicalKey][form]  — workspace override
 *   2. collectionCtx.labels[form]                         — collection default
 *   3. canonicalKey literal                               — fallback (no throw)
 *
 * Pure function. No Convex imports. No side effects. (PAT-230)
 *
 * @param canonicalKey - e.g. 'work_package', 'decision'
 * @param form - e.g. 'singular', 'plural', 'verb_start'
 * @param workspaceCtx - optional workspace-level override context
 * @param collectionCtx - optional collection-level default context
 * @returns resolved label string
 */
export function getCollectionLabel(
	canonicalKey: string,
	form: string,
	workspaceCtx?: WorkspaceVocabCtx,
	collectionCtx?: CollectionVocabCtx,
): string {
	// Layer 1: workspace override
	const wsLabels = workspaceCtx?.collectionLabels?.[canonicalKey];
	if (wsLabels) {
		const wsVal = (wsLabels as Record<string, string | undefined>)[form];
		if (wsVal !== undefined && wsVal.length > 0) return wsVal;
	}

	// Layer 2: explicit collection ctx (granular call sites — handshake projector)
	const colLabels = collectionCtx?.labels;
	if (colLabels) {
		const colVal = (colLabels as Record<string, string | undefined>)[form];
		if (colVal !== undefined && colVal.length > 0) return colVal;
	}

	// Layer 2.5: collection defaults from WorkspaceVocabCtx (threaded via chain.getVocabulary)
	// New: wires the dead Layer 2 path for consumers that don't pass explicit collectionCtx.
	const defaultLabels = workspaceCtx?.collectionDefaults?.[canonicalKey];
	if (defaultLabels) {
		const defVal = (defaultLabels as Record<string, string | undefined>)[form];
		if (defVal !== undefined && defVal.length > 0) return defVal;
	}

	// Layer 3: canonicalKey literal fallback
	return canonicalKey;
}

/**
 * Replace all {{vocab:<canonicalKey>.<form>}} tokens in a string.
 *
 * For each token, calls getCollectionLabel with the provided contexts.
 * STD-253.
 */
export function replaceVocabTokens(
	body: string,
	workspaceCtx?: WorkspaceVocabCtx,
	collectionCtxMap?: Map<string, CollectionVocabCtx>,
): string {
	if (!body || body.length === 0) return body;

	const regex = new RegExp(VOCAB_TOKEN_REGEX.source, 'g');

	return body.replace(regex, (_match, canonicalKey: string, form: string) => {
		const collectionCtx = collectionCtxMap?.get(canonicalKey);
		return getCollectionLabel(canonicalKey, form, workspaceCtx, collectionCtx);
	});
}
